import { useEffect, useRef, useState } from 'react';
import { resumeService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { ResumeRequest } from '../types';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutosave(data: ResumeRequest | null, interval = 2000) {
  const { user } = useAuth();
  const [status, setStatus] = useState<SaveStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevRef = useRef<string>('');

  useEffect(() => {
    if (!data || !user?.id) return;

    const serialized = JSON.stringify(data);
    if (serialized === prevRef.current) return;
    prevRef.current = serialized;

    if (timerRef.current) clearTimeout(timerRef.current);

    setStatus('saving');
    timerRef.current = setTimeout(async () => {
      try {
        await resumeService.saveProfile(user.id, data);
        setStatus('saved');
      } catch {
        setStatus('error');
      }
      const clearTimer = setTimeout(() => setStatus('idle'), 3000);
      timerRef.current = clearTimer;
    }, interval);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, user?.id, interval]);

  return status;
}
